import React from "react";
import Dialog from "@mui/material/Dialog";
import { RxCross2 } from "react-icons/rx";
import { TbFileDownload } from "react-icons/tb";
import { useGetPanPreviousFormsQuery } from "src/services/PANService";
import { formatDateAndTime } from "src/utils/dateAndTime";

type Props = {
  panId: string;
  onClose(): void;
  /** Downloads one PDF; `index` numbers the old forms, 1 = the first ever filled. */
  onDownload(url: string, index: number): void;
};

const when = (value?: string) =>
  value ? formatDateAndTime(value, "DD MMM yyyy hh:mm A") : "-";

/**
 * The PAN form PDFs that refills replaced. A refill keeps the earlier PDF
 * instead of overwriting it, and only roles holding VIEW_OLD_PAN_FORM may open
 * this list. Everyone else only ever sees the latest form. The API checks the
 * permission too, so this component is only mounted behind that AuthHOC.
 */
const OldPanFormsDialog = ({ panId, onClose, onDownload }: Props) => {
  const { data, isFetching, isError } = useGetPanPreviousFormsQuery(panId, {
    skip: !panId,
    refetchOnMountOrArgChange: true,
  });
  const rows: any[] = data?.data?.previousForms || [];
  const total = rows.length;

  return (
    <Dialog
      open
      onClose={onClose}
      sx={{ "& .MuiDialog-container .MuiPaper-root": { width: "100%", maxWidth: "640px" } }}
    >
      <div className="flex justify-end pt-1 pr-2 cursor-pointer" onClick={onClose}>
        <span className="bg-slate-400 rounded-full p-1 text-white hover:bg-red-400 transition-all">
          <RxCross2 size="1.2em" />
        </span>
      </div>
      <h3 className="flex justify-center font-semibold text-[18px] pb-2 border-b-2">
        Old PAN Forms
      </h3>

      <div className="p-3 text-[14px]">
        {isFetching ? (
          <div className="text-center text-slate-500 py-4">Loading...</div>
        ) : isError ? (
          <div className="text-center text-red-500 py-4">
            Could not load the old PAN forms.
          </div>
        ) : !total ? (
          <div className="text-center text-slate-500 py-4">
            No older PAN form is saved for this application.
          </div>
        ) : (
          <table className="w-full border">
            <thead>
              <tr className="bg-slate-100 text-left">
                <th className="p-2 border">#</th>
                <th className="p-2 border">Filled</th>
                <th className="p-2 border">Replaced (Refill)</th>
                <th className="p-2 border"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                // Rows come newest first; number them oldest = 1.
                const index = total - i;
                return (
                  <tr key={`${row.panFormFile}-${i}`}>
                    <td className="p-2 border">{index}</td>
                    <td className="p-2 border">
                      <div>{when(row.generatedAt)}</div>
                      <div className="text-[12px] text-slate-500">
                        {row.generatedByName || "-"}
                      </div>
                    </td>
                    <td className="p-2 border">
                      <div>{when(row.replacedAt)}</div>
                      <div className="text-[12px] text-slate-500">
                        {row.replacedByName || "-"}
                      </div>
                    </td>
                    <td className="p-2 border text-center">
                      <button
                        type="button"
                        className="flex items-center gap-1 text-primary-main hover:underline"
                        onClick={() => onDownload(row.panFormFile, index)}
                      >
                        <TbFileDownload /> OLD FORM
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Dialog>
  );
};

export default OldPanFormsDialog;
